import React, { useMemo } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { TanTable } from './TanTable';
import { useHistoryPanelData } from '../hooks/useHistoryPanelData';
import { formatIsoDateOnly } from '../utils/dateUtils';

interface StorageRecord {
  lirecno: number;
  invoicefor: string | null;
  mrnum: string;
  qty: number;
  /** V1-formatted qty: 2 decimals for bill-to 7620 (McGrann), integer otherwise. */
  qtydisplay: string;
  uom: string;
  rate: number | null;
  quote: string;
  invoicingtype: string;
  whse: string;
  invoicenum: string;
  invoicedate: string | null;
}

interface StorageResponse {
  count: number;
  totalCharges: number;
  records: StorageRecord[];
}

interface StoragePanelProps {
  pronumber: number | null;
  /** Defer fetching until the panel is actually shown. */
  active?: boolean;
  /** Bump this number to trigger a refetch from the parent. */
  refreshNonce?: number;
}

export const StoragePanel: React.FC<StoragePanelProps> = ({
  pronumber,
  active = true,
  refreshNonce = 0,
}) => {
  const { data, loading, error } = useHistoryPanelData<StorageResponse>(
    pronumber ? `/orders/${pronumber}/storage-history` : null,
    active,
    refreshNonce,
    'Failed to fetch storage history'
  );

  const columns = useMemo<ColumnDef<StorageRecord>[]>(
    () => [
      {
        accessorKey: 'invoicefor',
        header: 'Invoice For',
        size: 110,
        cell: (info) => formatIsoDateOnly(info.getValue() as string | null) || '-',
      },
      {
        accessorKey: 'mrnum',
        header: 'MR#',
        size: 100,
        cell: (info) => (info.getValue() as string) || '-',
      },
      {
        // Sorts numerically on qty; displays the backend's V1-formatted string
        // (bill-to 7620 keeps fixed 2 decimals, e.g. "2.50").
        accessorKey: 'qty',
        header: 'Qty',
        size: 80,
        cell: (info) => {
          const record = info.row.original;
          if (record.qty == null) return '-';
          return record.qtydisplay || String(record.qty);
        },
      },
      {
        accessorKey: 'uom',
        header: 'UoM',
        size: 100,
        cell: (info) => (info.getValue() as string) || '-',
      },
      {
        accessorKey: 'rate',
        header: 'Rate',
        size: 90,
        cell: (info) => {
          const v = info.getValue() as number | null;
          return v == null ? '-' : `$${v.toFixed(2)}`;
        },
      },
      {
        accessorKey: 'quote',
        header: 'Quote',
        size: 130,
        cell: (info) => {
          const v = info.getValue() as string;
          if (!v) return '-';
          return (
            <div className="truncate" title={v}>
              {v}
            </div>
          );
        },
      },
      {
        accessorKey: 'invoicingtype',
        header: 'Invoice Type',
        size: 110,
        cell: (info) => (info.getValue() as string) || '-',
      },
      {
        accessorKey: 'whse',
        header: 'Whse',
        size: 80,
        cell: (info) => (info.getValue() as string) || '-',
      },
      {
        accessorKey: 'invoicenum',
        header: 'Invoice#',
        size: 100,
        cell: (info) => (info.getValue() as string) || '-',
      },
      {
        accessorKey: 'invoicedate',
        header: 'Invoice Date',
        size: 110,
        cell: (info) => formatIsoDateOnly(info.getValue() as string | null) || '-',
      },
    ],
    []
  );

  return (
    <div className="h-full flex flex-col">
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 mb-3">
          <p className="text-red-700 dark:text-red-400 text-sm">{error}</p>
        </div>
      )}
      <div className="flex-1 min-h-[200px]">
        {/* No defaultSorting: the backend orders rows by invoice group; TanTable
            preserves input order when sorting state is empty. */}
        <TanTable
          data={data?.records || []}
          columns={columns}
          loading={loading}
          cardTitle="Storage"
          searchPlaceholder="Search storage..."
          settingsKey="pro-history-storage"
          fillContainer
          enableSearch
          enableReset
          enableShowHideCols
          enableColumnFilters
          enableZebraStriping={false}
        />
      </div>
      <div className="mt-2 text-right text-sm font-medium text-body">
        Total Charges: ${(data?.totalCharges ?? 0).toFixed(2)}
      </div>
    </div>
  );
};

export default StoragePanel;
